import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {
  InventoryState,
  FetchInventoryItemsResponse,
  FetchInventoryItemByIdResponse,
  FetchAllInventoryItemsResponse,
} from './types'
import {
  fetchInventoryItemById,
  fetchInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  assignInventoryItem,
  unassignInventoryItem,
  updateMaintenance,
  fetchAllInventoryItems,
} from './service'
import {ResponseError} from '../commonTypes'

const initialState: InventoryState = {
  inventoryItemForEdit: null,
  inventoryItems: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  actionLoading: false,
  error: null,
}

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    resetInventoryItems: (state: InventoryState) => {
      state.inventoryItems = []
      state.total = 0
      state.totalPages = 0
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInventoryItems.pending, (state: InventoryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchInventoryItems.rejected, (state: InventoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.listLoading = false
    })
    builder.addCase(
      fetchInventoryItems.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchInventoryItemsResponse>) => {
        state.listLoading = false
        state.error = null
        const {inventoryItems, total, totalPages} = action.payload
        state.inventoryItems = inventoryItems
        state.total = total
        state.totalPages = totalPages
      }
    )

    builder.addCase(fetchAllInventoryItems.pending, (state: InventoryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(
      fetchAllInventoryItems.rejected,
      (state: InventoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.listLoading = false
      }
    )
    builder.addCase(
      fetchAllInventoryItems.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchAllInventoryItemsResponse>) => {
        state.listLoading = false
        state.error = null
        const {inventoryItems} = action.payload
        state.inventoryItems = inventoryItems
      }
    )

    builder.addCase(fetchInventoryItemById.pending, (state: InventoryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(
      fetchInventoryItemById.rejected,
      (state: InventoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.listLoading = false
      }
    )
    builder.addCase(
      fetchInventoryItemById.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchInventoryItemByIdResponse>) => {
        state.inventoryItemForEdit = action.payload.inventoryItem
        state.listLoading = false
        state.error = null
        state.inventoryItemForEdit = action.payload.inventoryItem
      }
    )

    builder.addCase(createInventoryItem.pending, (state: InventoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(createInventoryItem.rejected, (state: InventoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      createInventoryItem.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchInventoryItemByIdResponse>) => {
        state.inventoryItemForEdit = action.payload.inventoryItem
        state.actionLoading = false
        state.error = null
      }
    )

    builder.addCase(updateInventoryItem.pending, (state: InventoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(updateInventoryItem.rejected, (state: InventoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      updateInventoryItem.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchInventoryItemByIdResponse>) => {
        const updatedItem = action.payload.inventoryItem
        state.inventoryItemForEdit = updatedItem
        state.actionLoading = false
        state.error = null
        
        // Update the specific item in the inventoryItems array instead of refreshing the entire list
        const itemIndex = state.inventoryItems.findIndex(item => item._id === updatedItem._id)
        if (itemIndex !== -1) {
          state.inventoryItems[itemIndex] = updatedItem
        }
      }
    )

    builder.addCase(deleteInventoryItem.pending, (state: InventoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(deleteInventoryItem.rejected, (state: InventoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      deleteInventoryItem.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchInventoryItemByIdResponse>) => {
        const deletedItem = action.payload.inventoryItem
        state.inventoryItemForEdit = deletedItem
        state.actionLoading = false
        state.error = null
        
        // Remove the deleted item from the inventoryItems array
        state.inventoryItems = state.inventoryItems.filter(item => item._id !== deletedItem._id)
        // Update total count
        state.total = Math.max(0, state.total - 1)
      }
    )

    builder.addCase(assignInventoryItem.pending, (state: InventoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(assignInventoryItem.rejected, (state: InventoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      assignInventoryItem.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchInventoryItemByIdResponse>) => {
        const updatedItem = action.payload.inventoryItem
        state.inventoryItemForEdit = updatedItem
        state.actionLoading = false
        state.error = null
        
        // Update the specific item in the inventoryItems array
        const itemIndex = state.inventoryItems.findIndex(item => item._id === updatedItem._id)
        if (itemIndex !== -1) {
          state.inventoryItems[itemIndex] = updatedItem
        }
      }
    )

    builder.addCase(unassignInventoryItem.pending, (state: InventoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(unassignInventoryItem.rejected, (state: InventoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      unassignInventoryItem.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchInventoryItemByIdResponse>) => {
        const updatedItem = action.payload.inventoryItem
        state.inventoryItemForEdit = updatedItem
        state.actionLoading = false
        state.error = null
        
        // Update the specific item in the inventoryItems array
        const itemIndex = state.inventoryItems.findIndex(item => item._id === updatedItem._id)
        if (itemIndex !== -1) {
          state.inventoryItems[itemIndex] = updatedItem
        }
      }
    )

    builder.addCase(updateMaintenance.pending, (state: InventoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(updateMaintenance.rejected, (state: InventoryState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      updateMaintenance.fulfilled,
      (state: InventoryState, action: PayloadAction<FetchInventoryItemByIdResponse>) => {
        const updatedItem = action.payload.inventoryItem
        state.inventoryItemForEdit = updatedItem
        state.actionLoading = false
        state.error = null
        
        // Update the specific item in the inventoryItems array
        const itemIndex = state.inventoryItems.findIndex(item => item._id === updatedItem._id)
        if (itemIndex !== -1) {
          state.inventoryItems[itemIndex] = updatedItem
        }
      }
    )
  },
})

export default inventorySlice.reducer
export const actions = inventorySlice.actions
